import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  FormGroup,
  FormInput,
  Slider,
  StyleButtons,
  AdvancedColorInput,
  ToggleSwitch,
  NavButtons,
} from '../common';

const ContentSection = ({
  settings,
  onUpdate,
  onNavigate,
  regmailEnabled,
  languages = [],
}) => {
  const ctaStyleSlots = Array.isArray(settings.ctaStyleSlots)
    ? settings.ctaStyleSlots
    : settings.ctaButtons || [];

  const defaultSectionTitleStyle = {
    id: 'section-title-style',
    text: '',
    body: '',
    color: settings.linkColor || '#00519e',
    fontSize: 18,
    styles: ['bold'],
  };

  const baseSectionTitleStyle =
    Array.isArray(settings.sectionTitles) && settings.sectionTitles.length
      ? settings.sectionTitles[0]
      : defaultSectionTitleStyle;

  const updateSectionTitleStyle = (field, value) => {
    const existing = Array.isArray(settings.sectionTitles)
      ? settings.sectionTitles
      : [];
    const seed = existing[0] || defaultSectionTitleStyle;
    const nextStyle = { ...seed, [field]: value };
    const nextTitles = existing.length
      ? existing.map(item => ({
          ...item,
          color: nextStyle.color,
          fontSize: nextStyle.fontSize,
          styles: Array.isArray(nextStyle.styles)
            ? nextStyle.styles
            : ['bold'],
          level: nextStyle.level,
        }))
      : [
          {
            ...nextStyle,
            id: seed.id || uuidv4(),
            text: '',
            body: '',
          },
        ];
    onUpdate('sectionTitles', nextTitles);
  };

  const defaultCtaStyle = {
    id: 'cta-style',
    text: '',
    url: '',
    backgroundColor: settings.linkColor || '#00519e',
    textColor: '#ffffff',
    fontSize: 14,
    paddingX: 20,
    paddingY: 10,
    borderRadius: 6,
    styles: ['bold'],
  };

  const baseCtaStyle =
    Array.isArray(ctaStyleSlots) && ctaStyleSlots.length
      ? ctaStyleSlots[0]
      : defaultCtaStyle;

  const updateCtaStyle = (field, value) => {
    const existing = Array.isArray(ctaStyleSlots) ? ctaStyleSlots : [];
    const seed = existing[0] || defaultCtaStyle;
    const nextStyle = { ...seed, [field]: value };
    const nextButtons = existing.length
      ? existing.map(item => ({
          ...item,
          backgroundColor: nextStyle.backgroundColor,
          textColor: nextStyle.textColor,
          fontSize: nextStyle.fontSize,
          paddingX: nextStyle.paddingX,
          paddingY: nextStyle.paddingY,
          borderRadius: nextStyle.borderRadius,
          styles: Array.isArray(nextStyle.styles)
            ? nextStyle.styles
            : ['bold'],
        }))
      : [{ ...nextStyle, id: seed.id || uuidv4() }];
    onUpdate('ctaStyleSlots', nextButtons);
  };

  const resolvedTitleStyles = Array.isArray(baseSectionTitleStyle.styles)
    ? baseSectionTitleStyle.styles
    : ['bold'];
  const titleIsNormal = resolvedTitleStyles.includes('normal');
  const previewTitleStyle = {
    color: baseSectionTitleStyle.color || settings.linkColor || '#00519e',
    fontSize: `${Number(baseSectionTitleStyle.fontSize) || 18}pt`,
    fontWeight: !titleIsNormal && resolvedTitleStyles.includes('bold') ? 700 : 400,
    fontStyle: !titleIsNormal && resolvedTitleStyles.includes('italic') ? 'italic' : 'normal',
    textDecoration: !titleIsNormal && resolvedTitleStyles.includes('underline') ? 'underline' : 'none',
  };

  const resolvedCtaStyles = Array.isArray(baseCtaStyle.styles)
    ? baseCtaStyle.styles
    : ['bold'];
  const ctaIsNormal = resolvedCtaStyles.includes('normal');
  const previewCtaStyle = {
    backgroundColor: baseCtaStyle.backgroundColor || settings.linkColor || '#00519e',
    color: baseCtaStyle.textColor || '#ffffff',
    fontSize: `${Number(baseCtaStyle.fontSize) || 14}pt`,
    padding: `${Number(baseCtaStyle.paddingY) || 10}px ${Number(baseCtaStyle.paddingX) || 20}px`,
    borderRadius: `${Number(baseCtaStyle.borderRadius) || 6}px`,
    fontWeight: !ctaIsNormal && resolvedCtaStyles.includes('bold') ? 700 : 400,
    fontStyle: !ctaIsNormal && resolvedCtaStyles.includes('italic') ? 'italic' : 'normal',
    textDecoration: !ctaIsNormal && resolvedCtaStyles.includes('underline') ? 'underline' : 'none',
  };

  const handleCalendarButtonToggle = value => {
    onUpdate('includeCalendarButton', value);
  };

  const languageOptions = Array.isArray(languages)
    ? languages.filter(lang => lang?.id || lang?.label)
    : [];

  const resolveLanguageValue = (map, langId) => {
    if (!map || !langId) return '';
    const normalized = String(langId).toLowerCase();
    const entryKey = Object.keys(map).find(
      key => String(key).toLowerCase() === normalized
    );
    return entryKey ? map[entryKey] : '';
  };

  const resolveLanguageEntryKey = (map, langId) => {
    if (!map || !langId || typeof map !== 'object') return null;
    const normalized = String(langId).toLowerCase();
    return (
      Object.keys(map).find(key => String(key).toLowerCase() === normalized) ||
      null
    );
  };

  const resolveEditableTranslationValue = (map, langId, fallback, defaults) => {
    const explicitKey = resolveLanguageEntryKey(map, langId);
    if (explicitKey) {
      return map[explicitKey] ?? '';
    }
    return (
      resolveLanguageValue(defaults, langId) ||
      fallback ||
      ''
    );
  };

  const updateLanguageMap = (field, langId, value) => {
    if (!langId) return;
    const existing =
      settings?.[field] && typeof settings[field] === 'object'
        ? settings[field]
        : {};
    const existingKey = resolveLanguageEntryKey(existing, langId);
    const targetKey = existingKey || langId;
    const next = { ...existing, [targetKey]: value };
    onUpdate(field, next);
  };

  const SALUTATION_DEFAULTS = {
    en: 'Dear ${p.firstName},',
    de: 'Guten Tag ${p.firstName} ${p.lastName}',
    fr: 'Chère/Cher ${p.firstName} ${p.lastName},',
    ar: '&{anrede}،',
  };
  const CLOSING_DEFAULTS = {
    en: 'Sincerely,',
    de: 'Mit freundlichen Grüßen,',
    fr: 'Cordialement,',
    ar: 'مع أطيب التحيات،',
  };
  const FOOTER_DEFAULTS = {
    en: '© EVENT 2026 - All rights reserved.',
    de: '© EVENT 2026 – Alle Rechte vorbehalten.',
    fr: '© EVENT 2026 – Tous droits réservés.',
    ar: '© EVENT 2026 – جميع الحقوق محفوظة.',
  };
  const CONFIDENTIALITY_DEFAULTS = {
    en: 'Confidentiality Note: This message is intended only for the use of the named recipient(s) and may contain confidential and/or privileged information. If you are not the intended recipient, please contact the sender and delete the message. Any unauthorized use of the information contained in this message is prohibited.',
    de: 'Vertraulichkeitshinweis: Diese Nachricht ist nur für die benannte(n) empfangende(n) Person(en) bestimmt und kann vertrauliche und/oder privilegierte Informationen enthalten. Falls Sie nicht die vorgesehene empfangende Person sind, kontaktieren Sie bitte die absendende Person und löschen Sie die Nachricht. Jede unbefugte Nutzung der in dieser Nachricht enthaltenen Informationen ist untersagt.',
    fr: 'Note de confidentialité: Ce message est destiné uniquement à l’usage du ou des destinataires désignés et peut contenir des informations confidentielles et/ou privilégiées. Si vous n’êtes pas le destinataire prévu, veuillez contacter l’expéditeur et supprimer le message. Toute utilisation non autorisée des informations contenues dans ce message est interdite.',
    ar: 'ملاحظة السرية: هذه الرسالة مخصصة فقط لاستخدام المستلم(ين) المحدد(ين) وقد تحتوي على معلومات سرية و/أو مميزة. إذا لم تكن المستلم المقصود، يرجى التواصل مع المرسل وحذف الرسالة. أي استخدام غير مصرح به للمعلومات الواردة في هذه الرسالة محظور.',
  };
  const confidentialityLanguageOptions = [
    { id: 'en', label: 'English' },
    { id: 'de', label: 'Deutsch' },
    { id: 'fr', label: 'Français' },
    { id: 'ar', label: 'العربية' },
  ];

  const resolveTranslationPlaceholder = (translations, langId, fallback, defaults) =>
    resolveLanguageValue(translations, langId) ||
    resolveLanguageValue(defaults, langId) ||
    fallback ||
    '';

  const resolveNextSectionKey = () => {
    if (settings.showCongressAppLinks) return 'congressAppLinks';
    if (settings.includeCalendarButton) return 'calendarButton';
    if (settings.includeWalletButtons) return 'wallet';
    if (regmailEnabled) return 'regmailCustomization';
    return 'images';
  };

  return (
    <>
      <FormGroup title="Display Options">
        <div className="flex flex-wrap items-center gap-6">
          <div className="max-w-xs">
            <ToggleSwitch
              label="Show Wallet Buttons"
              checked={settings.includeWalletButtons}
              onChange={v => onUpdate('includeWalletButtons', v)}
              tooltip="Shows Apple & Google Wallet buttons."
            />
          </div>
          <div className="max-w-xs">
            <ToggleSwitch
              label="Show Calendar Buttons"
              checked={settings.includeCalendarButton}
              onChange={handleCalendarButtonToggle}
              tooltip="Shows Add to Calendar buttons."
            />
          </div>
          <div className="max-w-xs">
            <ToggleSwitch
              label="Show Congress App Links"
              checked={settings.showCongressAppLinks}
              onChange={value => onUpdate('showCongressAppLinks', value)}
            />
          </div>
        </div>
      </FormGroup>

      <FormGroup title="Salutation & Closing">
        {languageOptions.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white/80 p-4 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
              ALTERNATE LANGUAGES
            </p>
            <div className="text-xs text-slate-500">
              Only configure these if that language is used.
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="hidden md:grid grid-cols-[160px_1fr_1fr] gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                <span>Language</span>
                <span>Salutation</span>
                <span>Closing line</span>
              </div>
              {languageOptions.map(lang => {
                const langId = lang.id || lang.label;
                const langLabel = lang.label || lang.id || 'Language';
                return (
                  <div
                    key={`salutation-${langId}`}
                    className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] gap-3 items-center"
                  >
                    <span className="text-xs font-semibold text-slate-600">
                      {langLabel}
                    </span>
                    <input
                      value={resolveLanguageValue(
                        settings.salutationTranslations,
                        langId
                      )}
                      onChange={e =>
                        updateLanguageMap(
                          'salutationTranslations',
                          langId,
                          e.target.value
                        )
                      }
                      placeholder={resolveTranslationPlaceholder(
                        settings.salutationTranslations,
                        langId,
                        settings.salutationInput,
                        SALUTATION_DEFAULTS
                      )}
                      className="form-input"
                    />
                    <input
                      value={resolveLanguageValue(
                        settings.closingTextTranslations,
                        langId
                      )}
                      onChange={e =>
                        updateLanguageMap(
                          'closingTextTranslations',
                          langId,
                          e.target.value
                        )
                      }
                      placeholder={resolveTranslationPlaceholder(
                        settings.closingTextTranslations,
                        langId,
                        settings.closingText,
                        CLOSING_DEFAULTS
                      )}
                      className="form-input"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </FormGroup>

      {settings.includeFooter !== false && (
        <FormGroup title="Footer Content">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Slider
                label="Footer Height"
                value={settings.footerPadding || 20}
                onChange={v => onUpdate('footerPadding', v)}
                min={10}
                max={100}
                unit="px"
              />
              <Slider
                label="Footer Font Size"
                value={settings.footerFontSize}
                onChange={v => onUpdate('footerFontSize', v)}
                min={8}
                max={24}
                unit="pt"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Footer Text Style
              </label>
              <StyleButtons
                styles={settings.footerStyles || ['normal']}
                onToggle={v => onUpdate('footerStyles', v)}
                targetId="footer"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="max-w-md">
                <ToggleSwitch
                  label="Include Confidentiality Note"
                  checked={Boolean(settings.includeConfidentialityNote)}
                  onChange={value => onUpdate('includeConfidentialityNote', value)}
                  tooltip="Adds a confidentiality notice before the footer."
                />
              </div>
              {settings.includeConfidentialityNote && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                  <Slider
                    label="Confidentiality Text Size"
                    value={settings.confidentialityNoteFontSize || settings.footerFontSize || 10}
                    onChange={v => onUpdate('confidentialityNoteFontSize', v)}
                    min={8}
                    max={20}
                    unit="pt"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confidentiality Text Style
                    </label>
                    <StyleButtons
                      styles={settings.confidentialityNoteStyles || ['normal']}
                      onToggle={v => onUpdate('confidentialityNoteStyles', v)}
                      targetId="confidentiality-note-style"
                    />
                  </div>
                </div>
              )}
            </div>
            {languageOptions.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4 space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  ALTERNATE LANGUAGES
                </p>
                <p className="text-xs text-slate-500">
                  Only configure these if that language is used.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {languageOptions.map(lang => {
                    const langId = lang.id || lang.label;
                    const langLabel = lang.label || lang.id || 'Language';
                    return (
                      <FormInput
                        key={`footer-${langId}`}
                        label={`${langLabel} footer text`}
                        value={resolveLanguageValue(
                          settings.footerTextTranslations,
                          langId
                        )}
                        onChange={e =>
                          updateLanguageMap(
                            'footerTextTranslations',
                            langId,
                            e.target.value
                          )
                        }
                        placeholder={resolveTranslationPlaceholder(
                          settings.footerTextTranslations,
                          langId,
                          settings.footerText,
                          FOOTER_DEFAULTS
                        )}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {settings.includeConfidentialityNote && (
              <div className="rounded-xl border border-slate-200 bg-white/80 p-4 space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  CONFIDENTIALITY NOTE LANGUAGES
                </p>
                <p className="text-xs text-slate-500">
                  This note will auto-switch using the text template language.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  {confidentialityLanguageOptions.map(lang => {
                    const langId = lang.id;
                    const langLabel = lang.label;
                    return (
                      <div key={`confidentiality-${langId}`}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {langLabel} confidentiality note
                        </label>
                        <textarea
                          value={resolveEditableTranslationValue(
                          settings.confidentialityNoteTranslations,
                            langId,
                            settings.confidentialityNote,
                            CONFIDENTIALITY_DEFAULTS
                          )}
                          onChange={e =>
                            updateLanguageMap(
                              'confidentialityNoteTranslations',
                              langId,
                              e.target.value
                            )
                          }
                          className="form-textarea min-h-[130px]"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </FormGroup>
      )}

      <FormGroup title="Section Titles">
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
            <span style={previewTitleStyle}>Section title preview</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AdvancedColorInput
              label="Title Color"
              value={baseSectionTitleStyle.color}
              onChange={v => updateSectionTitleStyle('color', v)}
            />
            <Slider
              label="Font Size"
              value={baseSectionTitleStyle.fontSize}
              onChange={v => updateSectionTitleStyle('fontSize', v)}
              min={12}
              max={48}
              unit="pt"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style
            </label>
            <StyleButtons
              styles={resolvedTitleStyles}
              onToggle={v => updateSectionTitleStyle('styles', v)}
              targetId="section-title-style"
            />
          </div>
          <p className="text-xs text-slate-500">
            Styling applies to every section title from text templates.
          </p>
        </div>
      </FormGroup>

      <FormGroup title="Call-to-Action Buttons">
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
            <span
              style={previewCtaStyle}
              className="inline-block font-semibold"
            >
              CTA preview
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AdvancedColorInput
              label="Background Color"
              value={baseCtaStyle.backgroundColor}
              onChange={v => updateCtaStyle('backgroundColor', v)}
            />
            <AdvancedColorInput
              label="Text Color"
              value={baseCtaStyle.textColor}
              onChange={v => updateCtaStyle('textColor', v)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CTA Text Style
            </label>
            <StyleButtons
              styles={resolvedCtaStyles}
              onToggle={v => updateCtaStyle('styles', v)}
              targetId="cta-text-style"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Slider
              label="Font Size"
              value={baseCtaStyle.fontSize}
              onChange={v => updateCtaStyle('fontSize', v)}
              min={10}
              max={24}
              unit="pt"
            />
            <Slider
              label="X Padding"
              value={baseCtaStyle.paddingX}
              onChange={v => updateCtaStyle('paddingX', v)}
              min={10}
              max={50}
            />
            <Slider
              label="Y Padding"
              value={baseCtaStyle.paddingY}
              onChange={v => updateCtaStyle('paddingY', v)}
              min={5}
              max={30}
            />
            <Slider
              label="Border Radius"
              value={baseCtaStyle.borderRadius}
              onChange={v => updateCtaStyle('borderRadius', v)}
              min={0}
              max={20}
            />
          </div>
          <p className="text-xs text-slate-500">
            Styling applies to every CTA button from text templates.
          </p>
        </div>
      </FormGroup>

      <NavButtons
        onPrev={() => onNavigate('appearance')}
        onNext={() => onNavigate(resolveNextSectionKey())}
      />
    </>
  );
};
export default ContentSection;
